import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { BaseQueryFn } from '@reduxjs/toolkit/query'
import { API_TIMEOUT, ENV } from '@/utils/constants'

/** Arguments accepted by RTK Query endpoints using {@link baseQuery}. */
export interface AxiosBaseQueryArgs {
  url: string
  method?: AxiosRequestConfig['method']
  data?: unknown
  // Accepts plain records as well as named param interfaces (e.g. `PackageBaseParams`),
  // mirroring the source app's `AxiosRequestConfig`-based `params` typing.
  params?: Record<string, unknown> | object
  headers?: Record<string, string>
}

/** Normalised error shape returned by {@link baseQuery}. */
export interface ApiError {
  status?: number
  data?: unknown
}

const axiosInstance = axios.create({
  baseURL: ENV.API_BASE_URL,
  timeout: API_TIMEOUT,
  // Send/receive the backend's HttpOnly bs_auth session cookie (same-origin).
  withCredentials: true,
})

interface StateWithAuthToken {
  auth?: { token?: string | null }
}

/**
 * Shared Axios-backed base query for RTK Query APIs. Injects the bearer token
 * from the auth slice and reads the base URL from the environment.
 */
export const baseQuery =
  (): BaseQueryFn<AxiosBaseQueryArgs, unknown, ApiError> =>
  async ({ url, method = 'get', data, params, headers }, api) => {
    try {
      const token = (api.getState() as StateWithAuthToken).auth?.token
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers,
        },
      })
      return { data: result.data }
    } catch (error) {
      const axiosError = error as AxiosError
      return {
        error: {
          status: axiosError.response?.status,
          data: axiosError.response?.data ?? axiosError.message,
        },
      }
    }
  }
